import { GoogleOAuthProvider } from '@react-oauth/google';
import React from 'react';
import { Card, Col, Form, Row, Stack } from 'react-bootstrap';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import Desktop from '../../assets/images/desktop.svg';
import logo from '../../assets/images/logo.png';
import logo3x from '../../assets/logo/leafio_original@3x.png';
import constant from '../../constants/constants';
import { getUser, isSupplier, login } from '../../services/authentication.service';
import { getOrderSummary } from '../../services/order.service';
import { isMobileScreen } from '../../utils/common';
import Button from '../common/button';
import FormInput from '../common/FormInput';
import toasterNotification from '../common/toasterNotification';
import useValidator from '../common/useValidator';
import Footer from '../layout/footer';
import GoogleLoginButton from './GoogleLoginButton';
import './signIn.scss';

const CONTACT_EMAILS = {
  admin: 'support@leafio.com',
  pharmacist: 'support@leafio.com',
  supplier: 'support@leafio.com',
};
const SignInForm = () => {
  const [loading, setLoading] = React.useState(false);
  const navigate = useNavigate();
  const [data, setData] = React.useState({
    email: '',
    password: '',
    remember: true,
    loginType: 'pharmacist',
  });
  const { loginType, expired} = useParams();
  const [validator, showValidationMessage] = useValidator();
  const [searchParams] = useSearchParams();

  React.useEffect(() => {
    const loggedIn = getUser();
    if (loggedIn && loggedIn.user ) {
      navigate('/products');
    }
    if (expired) {
      toasterNotification('You sessions has been expired, please login again.', 'error', {id:'session-expired'});
    }
  }, []);

  React.useEffect(() => {
    if (loginType && !constant.loginTypes.includes(loginType)) {
      navigate('/login');
    }
    setData({ ...data, loginType: loginType ? loginType : 'pharmacist' });
  }, [loginType]);

  const handleUserInput = (prop) => (e) => {
    setData({
      ...data,
      [e.target.name]: prop === 'checkbox' ? e.target.checked : e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validator.allValid()) {
      showValidationMessage(true);
      return;
    }
    setLoading(true);
    try {
      const loggedIn = await login(
        data.email.toLowerCase(),
        data.password,
        data.remember,
        data.loginType
      );
      const countResponse = await getOrderSummary();
      if (loggedIn && countResponse){
        if ((countResponse.data.totalOrders > 0 || countResponse.data.totalBackOrders > 0) && !isSupplier()) {
          navigate('/products');
        } else {
          navigate('/orders');
        }
      }
    } catch (err) {
      toasterNotification(err, 'error', 'login-failed');
      setLoading(false);
    }
  };
  const onKeyPressEvents=(e)=>{
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };


  return (
    <React.Fragment>
      <div className="login-wrapper w-100 d-none d-sm-none d-md-block">
        <Row className="p-0 m-0 view-height">
          <Col md="7" className="image-container col-md-7 row align-items-center justify-content-center mb-5 pb-5">
            <div className="mx-auto text-left w-50 text-white mb-5 pb-5">
              <h1 className="mb-3 fw-normal" data-testid='login-portal-message'>
                {data.loginType.charAt(0).toUpperCase() + data.loginType.slice(1)}{' '}Portal</h1>
              <h5 className='mr-5 fw-normal lh-base mb-5'>Leafio is the pharmacist’s one-stop-shop for ordering and managing Medical Cannabis prescriptions.</h5>
            </div>
          </Col>

          <Col md="5" className="form-container row px-5 justify-content-center align-items-center m-0">
            <div className='ps-5'>
              <Card className="px-5">
                <Card.Body className='mb-3 px-2'>
                  <div className="d-flex justify-content-center">
                    <a href="https://www.montu.com.au" target="blank">
                      <img src={logo} alt="Leafio" className="p-2 montu-logo" />
                    </a>
                  </div>
                  <h4 className="my-4 text-center fw-normal" data-testid='login-form-header'>
                    {data.loginType.charAt(0).toUpperCase()+data.loginType.slice(1)}{' '}Login
                  </h4>
                  {data.loginType === 'admin' ? (
                    <div className='admin-login-button'>
                      <GoogleOAuthProvider clientId={constant.GoogleAuthId}>
                        <GoogleLoginButton data={data} searchParams={searchParams} />
                      </GoogleOAuthProvider>
                    </div>
                  ) : (
                    <>
                      <div>
                        <Form.Label htmlFor="passwordLabel">
                          Email Address *
                        </Form.Label>
                        <FormInput
                          className="login-input"
                          placeholder= 'Email Address'
                          name="email"
                          type="text"
                          error={validator.message(
                            'email',
                            data.email,
                            'required|email'
                          )}
                          onChange={handleUserInput('text')}
                          value={data.email}
                          noLabel={true}
                          onKeyPress={(e) => onKeyPressEvents(e)}
                        />
                      </div>
                      <div className="mt-4 mb-2">
                        <Form.Label htmlFor="passwordLabel">
                            Password *
                        </Form.Label>
                        <FormInput
                          className="login-input"
                          placeholder='Password'
                          name="password"
                          type="password"
                          error={validator.message(
                            'password',
                            data.password,
                            'required'
                          )}
                          onChange={handleUserInput('text')}
                          value={data.password}
                          noLabel={true}
                          onKeyPress={(e) => onKeyPressEvents(e)}
                        />
                      </div>
                      <Link to={`/forgot-password/${data.loginType}`}>Forgot Password?</Link>
                      <div className="d-flex justify-content-around mt-4">
                        <Button
                          type="submit"
                          text="Login"
                          className="btn btn-secondary px-5 text-white"
                          loading={loading}
                          onClick={(e)=>handleSubmit(e)}
                          onKeyPress={(e) => onKeyPressEvents(e)}
                        />
                      </div>
                    </>
                  )}
                  <div className="mt-5 text-center">
                    <span className="">Contact: </span>
                    <Link to={`mailto:${CONTACT_EMAILS[data.loginType]}`}>
                      {' '}
                      {CONTACT_EMAILS[data.loginType]}
                    </Link>
                  </div>
                </Card.Body>
              </Card>
              {data.loginType === 'pharmacist' && (
                <div className="reg-link text-center mt-4">
                  <Link to="/register" className="text-white px-5">
                    Register New Pharmacy
                  </Link>
                </div>
              )}
            </div>
          </Col>
          <Footer isSignin={true}/>
        </Row>
      </div>

      {/* Mobile view*/}
      {isMobileScreen() && <SignInFormForMobile />}
    </React.Fragment>
  );
};



export const SignInFormForMobile = () => {
  return (
    <div className="login-wrapper h-100 d-block d-sm-none">
      <Stack gap={3} className={'h-100 mobile'}>
        <div className="d-inline-flex justify-content-center align-items-center p-4" style={{flex: 1}}>
          <div className="login-device-info">
            <div className="icon">
              <img src={Desktop} alt={'Info'}/>
            </div>
            <div className="info" data-testid='mobile-login-info'>
              Please use a desktop for the best experience
            </div>
          </div>
        </div>
        <div className="d-inline-flex align-items-center flex-direction-col logo-wrapper" style={{flex: 3}}>
          <div className='logo'>
            <img src={logo3x} alt="Leafio"/>
          </div>
          <div className='intro'>
            <div className="message">
              <span>
                  Leafio is the pharmacist’s one-stop-shop for ordering and managing Medical Cannabis prescriptions.
              </span>
            </div>
          </div>
        </div>
        <div className="d-inline-flex align-items-center flex-direction-col justify-content-end site-info" style={{flex: 1}}>
          <div className="support-number">Service Support Number: {constant.supportInfo.number}</div>
          <div className="copyrights">Copyrights Reserved <span className="fw-bold">Leafio</span></div>
        </div>
      </Stack>
    </div>
  );
};


export default SignInForm;
